import { Button } from '../../components/Button';
import { Divider } from '../../components/Divider';
import { ImageGallery } from '../../components/ImageGallery';
import { InfoNote } from '../../components/InfoNote';
import { Modal } from '../../components/Modal';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  Content,
  Description,
  ErrorRegion,
  FeatureList,
  FooterContent,
  GalleryCloseButton,
  GalleryFrame,
  HeaderRow,
  MetaText,
  NightPrice,
  PriceBlock,
  RoomName,
  StayPrice,
  TitleBlock,
  TotalDueAmount,
  TotalDueRow,
} from './RoomUpgradeDialog.styles';
import type { RoomUpgradeDialogProps, RoomUpgradeOffer } from './RoomUpgradeDialog.types';

export function roomUpgradeMetaText({ capacityText, bedsText }: RoomUpgradeOffer) {
  return [capacityText, bedsText].filter(Boolean).join(' • ');
}

export function RoomUpgradeDialog({
  open,
  upgradeRoom,
  headlinePrice = 'stay',
  upgrading = false,
  errorMessage,
  locale,
  onClose,
  onSkip,
  onUpgrade,
  labels = {},
}: RoomUpgradeDialogProps) {
  const {
    perNight = 'night',
    totalDue = 'Total due',
    skip = 'No thanks',
    upgrade = 'Upgrade',
    close = 'Close',
    previousImage,
    nextImage,
    goToImage,
  } = labels;

  const {
    name,
    imageUrl,
    images,
    description,
    features,
    priceDifference,
    pricePerStay,
    pricePerNight,
  } = upgradeRoom;
  const headline =
    headlinePrice === 'difference'
      ? `+ ${formatCurrency(priceDifference.amount, priceDifference.currencyCode, { locale })}`
      : pricePerStay && formatCurrency(pricePerStay.amount, pricePerStay.currencyCode, { locale });
  const metaText = roomUpgradeMetaText(upgradeRoom);
  const galleryImages = (images?.length ? images : [imageUrl])
    .filter((src): src is string => !!src)
    .map((src) => ({ src, alt: name }));
  const handleClose = upgrading ? () => undefined : onClose ?? onSkip;

  const footer = (
    <FooterContent>
      <Button variant="secondary" fullWidth onClick={onSkip} disabled={upgrading}>
        {skip}
      </Button>
      <Button variant="primary" fullWidth onClick={onUpgrade} loading={upgrading}>
        {upgrade}
      </Button>
    </FooterContent>
  );

  return (
    <Modal open={open} onClose={handleClose} ariaLabel={name} footer={footer} labels={{ close }}>
      <Content>
        <GalleryFrame>
          <ImageGallery
            images={galleryImages}
            sticky={false}
            prevLabel={previousImage}
            nextLabel={nextImage}
            goToImageLabel={goToImage}
          />
          <GalleryCloseButton type="button" onClick={handleClose} aria-label={close}>
            &#x2715;
          </GalleryCloseButton>
        </GalleryFrame>
        <HeaderRow>
          <TitleBlock>
            <RoomName>{name}</RoomName>
            {metaText && <MetaText>{metaText}</MetaText>}
          </TitleBlock>
          <PriceBlock>
            {headline && <StayPrice>{headline}</StayPrice>}
            {pricePerNight && (
              <NightPrice>
                {formatCurrency(pricePerNight.amount, pricePerNight.currencyCode, { locale })} /
                {perNight}
              </NightPrice>
            )}
          </PriceBlock>
        </HeaderRow>
        {description && <Description>{description}</Description>}
        {features && features.length > 0 && (
          <FeatureList>
            {features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </FeatureList>
        )}
        {pricePerStay && (
          <>
            <Divider />
            <TotalDueRow>
              <span>{totalDue}</span>
              <TotalDueAmount>
                {formatCurrency(pricePerStay.amount, pricePerStay.currencyCode, { locale })}
              </TotalDueAmount>
            </TotalDueRow>
          </>
        )}
        <ErrorRegion role="alert">
          {errorMessage && <InfoNote variant="danger" text={errorMessage} />}
        </ErrorRegion>
      </Content>
    </Modal>
  );
}
